const STREAM_TIMEOUT = Symbol('STREAM_TIMEOUT');

export async function* withAsyncGeneratorTimeout<T>(
	stream: AsyncGenerator<T>,
	{
		timeoutMs,
		onTimeout,
		isPaused,
	}: {
		timeoutMs: number;
		onTimeout: () => Error | undefined;
		/**
		 * Evaluated when the timer fires: if it returns `true`, the timer is
		 * re-armed instead of calling `onTimeout` (e.g. while reconnecting), so
		 * only continuous non-paused idleness counts as a timeout.
		 */
		isPaused?: () => boolean;
	},
): AsyncGenerator<T> {
	// Kept outstanding across timer re-arms; calling `next()` again while one is
	// pending is illegal on a generator.
	let nextPromise: Promise<IteratorResult<T>> | undefined;

	while (true) {
		if (!nextPromise) {
			nextPromise = stream.next();
		}

		let timeoutId: ReturnType<typeof setTimeout> | undefined;
		const timeout = new Promise<typeof STREAM_TIMEOUT>((resolve) => {
			timeoutId = setTimeout(() => resolve(STREAM_TIMEOUT), timeoutMs);
		});

		let result: IteratorResult<T> | typeof STREAM_TIMEOUT;
		try {
			result = await Promise.race([nextPromise, timeout]);
		} finally {
			clearTimeout(timeoutId);
			timeoutId = undefined;
		}

		if (result === STREAM_TIMEOUT) {
			// Paused (e.g. reconnecting): re-arm with the same pending `next()`.
			if (isPaused?.()) {
				continue;
			}

			const error = onTimeout();
			// Clean up the source stream after timing out.
			const returnPromise = stream.return?.(undefined);
			void returnPromise?.catch(() => undefined);

			if (error) {
				throw error;
			}

			return;
		}

		// Chunk arrived – advance to the next one.
		nextPromise = undefined;

		if (result.done) {
			return;
		}

		yield result.value;
	}
}
